import { supabase } from './client'

// Hours (24h) that get the night price: 18:00–04:00. Day hours (04:00–18:00)
// stay unpriced and fall back to turf.price_per_hour.
export const NIGHT_HOURS = [0, 1, 2, 3, 18, 19, 20, 21, 22, 23]

/** Current night price for a turf, or null if none set (flat pricing). */
export async function readNightPrice(turfId: string): Promise<null | number> {
  const { data } = await supabase
    .from('turf_pricing_schedule')
    .select('price')
    .eq('turf_id', turfId)
    .in('hour', NIGHT_HOURS)
    .limit(1)
    .maybeSingle()
  return data?.price ?? null
}

/**
 * Resolve the effective price for a slot.
 *
 * Order: explicit per-date slot price → weekly template price → turf base price.
 * 0 is a valid price; only null/undefined falls through to the next source.
 */
export function resolvePrice(
  slotPrice: null | number | undefined,
  templatePrice: null | number | undefined,
  basePrice: number,
): number {
  if (slotPrice != null) return slotPrice
  if (templatePrice != null) return templatePrice
  return basePrice
}

/**
 * Seed the weekly schedule's night hours with nightPrice across all 7 days.
 * Delete-first makes it idempotent; when nightPrice equals dayPrice the rows are
 * cleared (flat pricing) and every hour falls back to turf.price_per_hour.
 * ponytail: overwrites any per-hour night overrides in NIGHT_HOURS.
 */
export async function saveNightPrice(
  turfId: string,
  nightPrice: number,
  dayPrice: number,
): Promise<void> {
  await supabase
    .from('turf_pricing_schedule')
    .delete()
    .eq('turf_id', turfId)
    .in('hour', NIGHT_HOURS)
  if (nightPrice === dayPrice) return
  const rows = []
  for (let d = 0; d < 7; d++) {
    for (const h of NIGHT_HOURS)
      rows.push({ day_of_week: d, hour: h, price: nightPrice, turf_id: turfId })
  }
  const { error } = await supabase.from('turf_pricing_schedule').insert(rows)
  if (error) throw error
}

/** First hour (24h) considered "night" pricing. Day = 06:00–17:59, Night = 18:00+. */
export const NIGHT_START_HOUR = 18

/** Bookable hours, matching slot generation (06:00 through 22:00 start times). */
const BOOKABLE_HOURS = Array.from({ length: 17 }, (_, i) => 6 + i)

export interface DayNightPrice {
  /** Cheapest day-tier hour across the week. */
  day: number
  /** True when day and night resolve to the same price (flat pricing). */
  flat: boolean
  /** Cheapest night-tier hour across the week. */
  night: number
}

/**
 * Derive representative day/night per-hour prices from a turf's sparse weekly
 * pricing schedule. Hours without an explicit row fall back to basePrice, so
 * each tier is min(basePrice, cheapest override in that tier).
 *
 * Rows may span multiple days of the week; only the hour determines the tier.
 */
export function deriveDayNightPrice(
  scheduleRows: { hour: number; price: number }[],
  basePrice: number,
): DayNightPrice {
  // Cheapest override seen per hour (rows may span multiple days of the week).
  const minByHour = new Map<number, number>()
  for (const row of scheduleRows) {
    const cur = minByHour.get(row.hour)
    minByHour.set(row.hour, cur == null ? row.price : Math.min(cur, row.price))
  }
  // basePrice is a candidate only when the tier has an unpriced bookable hour —
  // a fully-priced night tier must not be dragged down to the day base price.
  const tierPrice = (hours: number[]): number => {
    let min = Infinity
    let hasUnpriced = false
    for (const h of hours) {
      const override = minByHour.get(h)
      if (override == null) hasUnpriced = true
      else min = Math.min(min, override)
    }
    if (hasUnpriced) min = Math.min(min, basePrice)
    return min === Infinity ? basePrice : min
  }
  const day = tierPrice(BOOKABLE_HOURS.filter((h) => h < NIGHT_START_HOUR))
  const night = tierPrice(BOOKABLE_HOURS.filter((h) => h >= NIGHT_START_HOUR))
  return { day, flat: day === night, night }
}
